import type { Rational, CellTag, Basin, FloodSnapshot, SliceState, VortexFloodSnapshot } from './FloodUtils.js';
import { rationalToNumber, rationalToString } from './FloodUtils.js';
import { parsePositionKey } from '../../core/GridGeometry.js';
import { GridSerializer } from '../../core/GridSerialization.js';

// Default serializer for 10x10 grid (used for debug output)
const defaultSerializer = new GridSerializer(10);

/** Convert position key "row,col" to display name "A0" */
function cellKeyToName(ck: string): string {
    return defaultSerializer.serializePosition(parsePositionKey(ck));
}

/** Format a vortex parent "sliceId:cellKey" into readable form */
function formatVortexParent(parent: string | null): string {
    if (parent === null) return 'faucet';
    const colonIdx = parent.lastIndexOf(':');
    if (colonIdx < 0) return cellKeyToName(parent);
    const sliceId = parent.slice(0, colonIdx);
    const cellKey = parent.slice(colonIdx + 1);
    const cellName = cellKeyToName(cellKey);
    if (sliceId === '') return cellName;
    // Show the slice ancestry for cross-slice parents
    const ancestryNames = sliceId.split('|').map(ck => cellKeyToName(ck)).join(',');
    return `${cellName}@[${ancestryNames}]`;
}

// Re-export types for consumers
export { Rational, CellTag, Basin, FloodSnapshot, SliceState, VortexFloodSnapshot, rationalToNumber };

export class FloodDebug {
    private timestamps: number[] = [];
    private snapshots: Map<number, FloodSnapshot> = new Map();
    private vortexSnapshots: Map<number, VortexFloodSnapshot> = new Map();
    private enabled: boolean = false;

    // Enable debug mode with specific timestamps to capture
    setTimestamps(timestamps: number[]): void {
        this.timestamps = [...timestamps].sort((a, b) => a - b);
        this.snapshots.clear();
        this.enabled = timestamps.length > 0;
    }

    isEnabled(): boolean {
        return this.enabled;
    }

    getTimestamps(): number[] {
        return this.timestamps;
    }

    clear(): void {
        this.snapshots.clear();
        this.vortexSnapshots.clear();
    }

    // Check if we should capture at the current time, and capture if so
    maybeCaptureSnapshot(
        currentTime: number,
        timestampIndex: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        flowState: Map<string, CellTag>,
        basins: Basin[]
    ): number {
        if (!this.enabled) return timestampIndex;

        while (timestampIndex < this.timestamps.length &&
               this.timestamps[timestampIndex] <= currentTime) {
            this.captureSnapshot(
                this.timestamps[timestampIndex],
                filled,
                waterLevel,
                flowState,
                basins
            );
            timestampIndex++;
        }
        return timestampIndex;
    }

    private captureSnapshot(
        time: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        flowState: Map<string, CellTag>,
        basins: Basin[]
    ): void {
        // Deep copy the state
        const filledCopy = new Set(filled);
        const waterLevelCopy = new Map<string, Rational>();
        for (const [k, v] of waterLevel) {
            waterLevelCopy.set(k, { ...v });
        }
        const flowStateCopy = new Map<string, CellTag>();
        for (const [k, v] of flowState) {
            flowStateCopy.set(k, { ...v } as CellTag);
        }
        const basinsCopy = basins.map(b => ({ cells: [...b.cells], rate: { ...b.rate } }));
        this.snapshots.set(time, {
            filled: filledCopy,
            waterLevel: waterLevelCopy,
            flowState: flowStateCopy,
            basins: basinsCopy
        });
    }

    // Log debug snapshots to console
    logSnapshots(): void {
        if (this.snapshots.size === 0) {
            console.log('No debug snapshots captured. Call setTimestamps() before simulation.');
            return;
        }
        console.log('\n=== Debug Snapshots ===');
        for (const [time, snapshot] of this.snapshots) {
            console.log(`\n--- t=${time} ---`);

            // Group flow state by type
            const drops: string[] = [];
            const crashes: string[] = [];
            const spreads: string[] = [];

            for (const [ck, tag] of snapshot.flowState) {
                const name = cellKeyToName(ck);
                const parentName = tag.parent ? cellKeyToName(tag.parent) : 'faucet';

                if (tag.type === 'drop') {
                    const rate = tag.rate ? rationalToString(tag.rate) : '?';
                    drops.push(`${name}(rate=${rate}, from=${parentName})`);
                } else if (tag.type === 'crash') {
                    const rate = rationalToString(tag.rate);
                    crashes.push(`${name}(rate=${rate}, from=${parentName})`);
                } else if (tag.type === 'spread') {
                    spreads.push(`${name}(from=${parentName})`);
                }
            }

            if (drops.length > 0) console.log(`  Drops: ${drops.join(', ')}`);
            if (crashes.length > 0) console.log(`  Crashes: ${crashes.join(', ')}`);
            if (spreads.length > 0) console.log(`  Spreads: ${spreads.join(', ')}`);

            // Show basins
            if (snapshot.basins.length > 0) {
                console.log(`  Basins:`);
                for (const basin of snapshot.basins) {
                    const cells = basin.cells.map(ck => cellKeyToName(ck)).sort().join(', ');
                    const rate = rationalToString(basin.rate);
                    console.log(`    [rate=${rate}] ${cells}`);
                }
            }

            // Show filled cells
            const filledNames = [...snapshot.filled].map(ck => cellKeyToName(ck)).sort();
            if (filledNames.length > 0) {
                console.log(`  Filled (${filledNames.length}): ${filledNames.join(', ')}`);
            }

            // Show partial fills
            const partials = [...snapshot.waterLevel.entries()]
                .filter(([ck]) => !snapshot.filled.has(ck))
                .map(([ck, level]) => `${cellKeyToName(ck)}:${(rationalToNumber(level) * 100).toFixed(1)}%`)
                .sort();
            if (partials.length > 0) {
                console.log(`  Partial: ${partials.join(', ')}`);
            }
        }
        console.log('\n=======================\n');
    }

    // Get snapshots for programmatic access
    getSnapshots(): Map<number, FloodSnapshot> {
        return this.snapshots;
    }

    // ── Vortex Snapshots ──────────────────────────────────────────

    // Check if we should capture a vortex snapshot at the current time
    maybeCaptureVortexSnapshot(
        currentTime: number,
        timestampIndex: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        slices: Map<string, SliceState>,
        basins: Basin[]
    ): number {
        if (!this.enabled) return timestampIndex;

        while (timestampIndex < this.timestamps.length &&
               this.timestamps[timestampIndex] <= currentTime) {
            this.captureVortexSnapshot(
                this.timestamps[timestampIndex],
                filled,
                waterLevel,
                slices,
                basins
            );
            timestampIndex++;
        }
        return timestampIndex;
    }

    private captureVortexSnapshot(
        time: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        slices: Map<string, SliceState>,
        basins: Basin[]
    ): void {
        const filledCopy = new Set(filled);
        const waterLevelCopy = new Map<string, Rational>();
        for (const [k, v] of waterLevel) {
            waterLevelCopy.set(k, { ...v });
        }
        const slicesCopy = new Map<string, SliceState>();
        for (const [id, slice] of slices) {
            const flowCopy = new Map<string, CellTag>();
            for (const [k, v] of slice.flowState) {
                flowCopy.set(k, { ...v } as CellTag);
            }
            slicesCopy.set(id, { ancestry: [...slice.ancestry], flowState: flowCopy });
        }
        const basinsCopy = basins.map(b => ({ cells: [...b.cells], rate: { ...b.rate } }));
        this.vortexSnapshots.set(time, {
            filled: filledCopy,
            waterLevel: waterLevelCopy,
            slices: slicesCopy,
            basins: basinsCopy
        });
    }

    // Log vortex snapshots grouped by slice
    logVortexSnapshots(): void {
        if (this.vortexSnapshots.size === 0) {
            console.log('No vortex debug snapshots captured. Call setTimestamps() before simulation.');
            return;
        }
        console.log('\n=== Vortex Debug Snapshots ===');
        for (const [time, snapshot] of this.vortexSnapshots) {
            console.log(`\n--- t=${time} ---`);

            // Display each slice
            for (const [sliceId, slice] of snapshot.slices) {
                const label = sliceId === ''
                    ? 'Slice [] (initial)'
                    : `Slice [${slice.ancestry.map(ck => cellKeyToName(ck)).join(', ')}]`;

                const drops: string[] = [];
                const crashes: string[] = [];
                const spreads: string[] = [];

                for (const [ck, tag] of slice.flowState) {
                    const name = cellKeyToName(ck);
                    const parentName = formatVortexParent(tag.parent);

                    if (tag.type === 'drop') {
                        const rate = tag.rate ? rationalToString(tag.rate) : '?';
                        drops.push(`${name}(rate=${rate}, from=${parentName})`);
                    } else if (tag.type === 'crash') {
                        const rate = rationalToString(tag.rate);
                        crashes.push(`${name}(rate=${rate}, from=${parentName})`);
                    } else if (tag.type === 'spread') {
                        spreads.push(`${name}(from=${parentName})`);
                    }
                }

                if (drops.length > 0 || crashes.length > 0 || spreads.length > 0) {
                    console.log(`  ${label}`);
                    if (drops.length > 0) console.log(`    Drops: ${drops.join(', ')}`);
                    if (crashes.length > 0) console.log(`    Crashes: ${crashes.join(', ')}`);
                    if (spreads.length > 0) console.log(`    Spreads: ${spreads.join(', ')}`);
                }
            }

            // Show basins
            if (snapshot.basins.length > 0) {
                console.log(`  Basins:`);
                for (const basin of snapshot.basins) {
                    const cells = basin.cells.map(ck => cellKeyToName(ck)).sort().join(', ');
                    const rate = rationalToString(basin.rate);
                    console.log(`    [rate=${rate}] ${cells}`);
                }
            }

            // Show filled cells
            const filledNames = [...snapshot.filled].map(ck => cellKeyToName(ck)).sort();
            if (filledNames.length > 0) {
                console.log(`  Filled (${filledNames.length}): ${filledNames.join(', ')}`);
            }

            // Show partial fills
            const partials = [...snapshot.waterLevel.entries()]
                .filter(([ck]) => !snapshot.filled.has(ck))
                .map(([ck, level]) => `${cellKeyToName(ck)}:${(rationalToNumber(level) * 100).toFixed(1)}%`)
                .sort();
            if (partials.length > 0) {
                console.log(`  Partial: ${partials.join(', ')}`);
            }
        }
        console.log('\n==============================\n');
    }

    getVortexSnapshots(): Map<number, VortexFloodSnapshot> {
        return this.vortexSnapshots;
    }

    // Log the drop parent tree and terminal crashes for each vortex snapshot
    logVortexDropTree(): void {
        if (this.vortexSnapshots.size === 0) return;

        for (const [time, snapshot] of this.vortexSnapshots) {
            console.log(`\n--- Drop tree at t=${time} ---`);

            for (const [sliceId, slice] of snapshot.slices) {
                const ancestry = sliceId === ''
                    ? '[]'
                    : `[${slice.ancestry.map(ck => cellKeyToName(ck)).join(', ')}]`;
                const drops: string[] = [];
                const crashes: string[] = [];
                for (const [ck, tag] of slice.flowState) {
                    if (tag.type === 'drop') {
                        const rate = tag.rate ? rationalToString(tag.rate) : '?';
                        drops.push(`${cellKeyToName(ck)} parent=${formatVortexParent(tag.parent)} rate=${rate}`);
                    } else if (tag.type === 'crash') {
                        crashes.push(`${cellKeyToName(ck)} parent=${formatVortexParent(tag.parent)} rate=${rationalToString(tag.rate)}`);
                    }
                }
                if (drops.length > 0 || crashes.length > 0) {
                    console.log(`  Slice ${ancestry}:`);
                    for (const d of drops) console.log(`    drop: ${d}`);
                    for (const c of crashes) console.log(`    crash: ${c}`);
                }
            }

            // Compute dropParents set
            const dropParents = new Set<string>();
            for (const [, slice] of snapshot.slices) {
                for (const [, tag] of slice.flowState) {
                    if (tag.type === 'drop' && tag.parent) {
                        dropParents.add(tag.parent);
                    }
                }
            }
            console.log(`  dropParents: ${[...dropParents].map(p => formatVortexParent(p)).join(', ')}`);

            // Show crashes NOT in dropParents (terminal)
            // Also detect crashes that spread into other slices via gates
            const crashGateSpreads = new Set<string>(); // crashKeys that have spreads in child slices
            for (const [sliceId, slice] of snapshot.slices) {
                for (const [, tag] of slice.flowState) {
                    if (tag.type === 'spread' && tag.parent) {
                        const colonIdx = tag.parent.lastIndexOf(':');
                        const parentSliceId = tag.parent.slice(0, colonIdx);
                        // If parent's slice differs from this spread's slice, it's a cross-slice spread
                        if (parentSliceId !== sliceId) {
                            crashGateSpreads.add(tag.parent);
                        }
                    }
                }
            }

            const terminal: string[] = [];
            for (const [sliceId, slice] of snapshot.slices) {
                for (const [ck, tag] of slice.flowState) {
                    if (tag.type === 'crash') {
                        const crashKey = `${sliceId}:${ck}`;
                        const ancestry = sliceId === ''
                            ? '[]'
                            : `[${slice.ancestry.map(c => cellKeyToName(c)).join(',')}]`;
                        const isDrop = dropParents.has(crashKey);
                        const hasGateSpread = crashGateSpreads.has(crashKey);
                        if (!isDrop) {
                            const flags = hasGateSpread ? ' [HAS GATE SPREAD]' : '';
                            terminal.push(`${cellKeyToName(ck)}@${ancestry} rate=${rationalToString(tag.rate)}${flags}`);
                        }
                    }
                }
            }
            console.log(`  terminal crashes: ${terminal.join(', ')}`);
        }
        console.log('');
    }
}
